import axios, { AxiosInstance } from 'axios';
import { Application, Template, Deployment, BuildJob, AIRequest, AIResponse, User, Organization } from '@/types';

class ApiClient {
  private static instance: ApiClient;
  private client: AxiosInstance;

  private constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /** Allow auth service to attach token interceptor */
  getClient(): AxiosInstance {
    return this.client;
  }

  private setupInterceptors(): void {
    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          const { status, data } = error.response;

          if (status === 429) {
            const retryAfter = error.response.headers['retry-after'];
            const message = `Rate limited. Please try again in ${retryAfter || '60'} seconds.`;
            throw new Error(message);
          }

          if (data?.error) {
            throw new Error(data.error);
          }
          if (data?.detail) {
            throw new Error(data.detail);
          }
        } else if (error.request) {
          throw new Error('Network error. Please check your connection.');
        }

        throw error;
      }
    );
  }

  // ── Projects (displayed as "Applications" in UI) ──

  async getApplications(params?: { page?: number; limit?: number; status?: string }): Promise<{ applications: Application[]; total: number }> {
    const response = await this.client.get('/projects', { params });
    // Backend returns { projects, total } — normalize to frontend naming
    return { applications: response.data.projects || response.data, total: response.data.total || 0 };
  }

  async getApplication(id: string): Promise<Application> {
    const response = await this.client.get(`/projects/${id}`);
    return response.data;
  }

  async createApplication(data: {
    name: string;
    description: string;
    templateId: string;
  }): Promise<Application> {
    // Clone from template to create a new project
    const response = await this.client.post(`/templates/${data.templateId}/clone`, {
      name: data.name,
      description: data.description,
    });
    return response.data;
  }

  async updateApplication(id: string, data: Partial<Application>): Promise<Application> {
    const response = await this.client.put(`/projects/${id}`, data);
    return response.data;
  }

  async deleteApplication(id: string): Promise<void> {
    await this.client.delete(`/projects/${id}`);
  }

  // ── Build & Deploy ──

  async buildApplication(id: string): Promise<BuildJob> {
    const response = await this.client.post(`/deployments/${id}/deploy`, {
      buildType: 'production',
    });
    return response.data;
  }

  async deployApplication(id: string, environment?: string): Promise<Deployment> {
    const response = await this.client.post(`/deployments/${id}/deploy`, { environment });
    return response.data;
  }

  async getDeploymentStatus(projectId: string, jobId: string): Promise<any> {
    const response = await this.client.get(`/deployments/${projectId}/status/${jobId}`);
    return response.data;
  }

  async getDeploymentHistory(projectId: string): Promise<Deployment[]> {
    const response = await this.client.get(`/deployments/${projectId}/history`);
    return response.data.deployments || response.data;
  }

  async rollbackDeployment(projectId: string, deploymentId: string): Promise<Deployment> {
    const response = await this.client.post(`/deployments/${projectId}/rollback/${deploymentId}`);
    return response.data;
  }

  // ── Templates ──

  async getTemplates(params?: { category?: string; search?: string; limit?: number; offset?: number }): Promise<{ templates: Template[]; total: number }> {
    const response = await this.client.get('/templates', { params });
    return { templates: response.data.templates || response.data, total: response.data.total || 0 };
  }

  async getPopularTemplates(limit?: number): Promise<Template[]> {
    const response = await this.client.get('/templates/popular', { params: { limit: limit || 10 } });
    return response.data.templates || response.data;
  }

  async getTemplate(id: string): Promise<Template> {
    const response = await this.client.get(`/templates/${id}`);
    return response.data;
  }

  async searchTemplates(query: string): Promise<Template[]> {
    const response = await this.client.get('/templates', { params: { search: query } });
    return response.data.templates || response.data;
  }

  // ── AI Services ──

  async modifyWithAI(request: AIRequest): Promise<AIResponse> {
    const response = await this.client.post('/ai/modify', {
      code: request.context?.currentCode,
      prompt: request.prompt,
      language: request.context?.framework,
    });
    return response.data;
  }

  async quickModifyWithAI(data: {
    templateId: string;
    prompt: string;
    features?: string[];
    style?: { framework?: string; uiLibrary?: string; colorScheme?: string };
  }): Promise<AIResponse> {
    const response = await this.client.post('/ai/quick-modify', data);
    return response.data;
  }

  async generateWithAI(request: AIRequest): Promise<AIResponse> {
    const response = await this.client.post('/ai/generate', {
      prompt: request.prompt,
      language: request.context?.framework,
      context: request.context,
    });
    return response.data;
  }

  async getAIJobStatus(jobId: string): Promise<any> {
    const response = await this.client.get(`/ai/status/${jobId}`);
    return response.data;
  }

  async getAISuggestions(projectId: string): Promise<any> {
    const response = await this.client.get(`/ai/suggestions/${projectId}`);
    return response.data;
  }

  // ── Auth ──

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  async verifyToken(): Promise<{ valid: boolean; user?: any }> {
    const response = await this.client.get('/auth/verify');
    return response.data;
  }

  async register(data: { name: string; email: string; password: string }): Promise<User> {
    const response = await this.client.post('/auth/register', data);
    return response.data.user;
  }

  async syncUser(data: { id: string; email: string; name?: string }): Promise<User> {
    const response = await this.client.post('/auth/callback', data);
    return response.data.user;
  }

  // ── Files ──

  async getProjectFiles(projectId: string): Promise<any[]> {
    const response = await this.client.get(`/files/project/${projectId}`);
    return response.data.files || response.data;
  }

  async getFileContent(fileId: string): Promise<string> {
    const response = await this.client.get(`/files/content/${fileId}`);
    return response.data.content;
  }

  async getFileContentByPath(projectId: string, filePath: string): Promise<string> {
    // Try direct path-based lookup first to avoid fetching the full file list
    try {
      const response = await this.client.get(`/files/content-by-path`, {
        params: { projectId, path: filePath },
      });
      return response.data.content;
    } catch {
      // Fallback: fetch file list and resolve ID (for older API versions)
      const files = await this.getProjectFiles(projectId);
      const file = files.find((f: any) => f.path === filePath);
      if (!file) return '';
      const response = await this.client.get(`/files/content/${file.id}`);
      return response.data.content;
    }
  }

  async uploadFile(projectId: string, file: File, filePath: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', filePath);
    formData.append('projectId', projectId);

    const response = await this.client.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteFile(fileId: string): Promise<void> {
    await this.client.delete(`/files/${fileId}`);
  }

  async saveFileContent(projectId: string, path: string, content: string): Promise<any> {
    const response = await this.client.put(`/files/save`, {
      projectId,
      path,
      content,
    });
    return response.data;
  }

  // ── Dashboard ──

  async getDashboardStats(): Promise<{
    totalApplications: number;
    activeDeployments: number;
    buildSuccess: number;
    apiUsage: number;
  }> {
    // Compute from projects list since no dedicated endpoint exists.
    // Fetch all applications (status filter for deployed count).
    try {
      const { applications, total } = await this.getApplications();
      const deployed = applications.filter(
        (a) => a.status === 'deployed' || a.status === ('DEPLOYED' as any)
      ).length;
      return {
        totalApplications: total || applications.length,
        activeDeployments: deployed,
        buildSuccess: 0,
        apiUsage: 0,
      };
    } catch {
      return { totalApplications: 0, activeDeployments: 0, buildSuccess: 0, apiUsage: 0 };
    }
  }

  // ── Health ──

  async healthCheck(): Promise<{ status: string; services: Record<string, boolean> }> {
    const response = await this.client.get('/health');
    return response.data;
  }
}

export const apiClient = ApiClient.getInstance();
